import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { 
  Handshake, 
  Send, 
  Inbox,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  MessageSquare,
  RefreshCw,
  AlertCircle,
  Loader2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const CollaborationRequests = ({ onViewOrganization }) => {
  const { t } = useTranslation();

  const collaborationTypeLabels = {
    partnership: t('collaboration_requests.type_partnership'),
    knowledge_sharing: t('collaboration_requests.type_knowledge_sharing'),
    joint_project: t('collaboration_requests.type_joint_project'),
    resource_sharing: t('collaboration_requests.type_resource_sharing'),
    mentorship: t('collaboration_requests.type_mentorship'),
    other: t('collaboration_requests.type_other')
  };

  const statusConfig = {
    pending: { label: t('collaboration_requests.status_pending'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    accepted: { label: t('collaboration_requests.status_accepted'), color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { label: t('collaboration_requests.status_rejected'), color: 'bg-red-100 text-red-800', icon: XCircle },
    cancelled: { label: t('collaboration_requests.status_cancelled'), color: 'bg-gray-100 text-gray-600', icon: X }
  };

  const [requests, setRequests] = useState({ sent: [], received: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [responding, setResponding] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/collaboration-requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/collaboration-requests/stats/summary`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch collaboration requests:', error);
      toast.error(t('collaboration_requests.toast_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleRespond = async (accept) => {
    if (!selectedRequest) return;
    
    setResponding(true);
    try {
      await axios.patch(`${API}/api/collaboration-requests/${selectedRequest.id}/respond`, {
        status: accept ? 'accepted' : 'rejected',
        response_message: responseMessage || null
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(accept ? t('collaboration_requests.toast_accepted') : t('collaboration_requests.toast_rejected'));
      setShowResponseDialog(false);
      setSelectedRequest(null);
      setResponseMessage('');
      fetchRequests();
    } catch (error) {
      console.error('Failed to respond to request:', error);
      toast.error(error.response?.data?.detail || t('collaboration_requests.toast_respond_failed'));
    } finally {
      setResponding(false);
    }
  };

  const handleCancel = async (requestId) => {
    try {
      await axios.patch(`${API}/api/collaboration-requests/${requestId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(t('collaboration_requests.toast_cancelled'));
      fetchRequests();
    } catch (error) {
      console.error('Failed to cancel request:', error);
      toast.error(error.response?.data?.detail || t('collaboration_requests.toast_cancel_failed'));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const RequestCard = ({ request, isSent }) => {
    const status = statusConfig[request.status] || statusConfig.pending;
    const StatusIcon = status.icon;
    const orgName = isSent ? request.receiver_name : request.sender_name;
    const orgId = isSent ? request.receiver_actor_id : request.sender_actor_id;

    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Handshake className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <h4 className="font-medium text-sm truncate">{request.title}</h4>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                <Building2 className="h-3 w-3" />
                <span className="font-medium">{isSent ? t('collaboration_requests.to_label') : t('collaboration_requests.from_label')}:</span>
                <button
                  onClick={() => onViewOrganization && onViewOrganization(orgId)}
                  className="text-blue-600 hover:underline truncate"
                >
                  {orgName}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className={`${status.color} text-xs`}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {status.label}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {collaborationTypeLabels[request.collaboration_type] || request.collaboration_type}
                </Badge>
              </div>

              <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                {request.message}
              </p>

              {request.response_message && (
                <div className="bg-gray-50 rounded p-2 mt-2">
                  <p className="text-xs text-gray-500 font-medium mb-1">{t('collaboration_requests.response_label')}</p>
                  <p className="text-xs text-gray-700">{request.response_message}</p>
                </div>
              )}

              <p className="text-xs text-gray-500 mt-2">
                <Clock className="h-3 w-3 inline mr-1" />
                {formatDate(request.created_at)}
                {request.responded_at && (
                  <span className="ml-2">
                    • {t('collaboration_requests.responded_label', { date: formatDate(request.responded_at) })}
                  </span>
                )}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {!isSent && request.status === 'pending' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowResponseDialog(true);
                    }}
                    className="bg-green-700 hover:bg-green-800"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('collaboration_requests.accept_btn')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowResponseDialog(true);
                    }}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    {t('collaboration_requests.decline_btn')}
                  </Button>
                </>
              )}
              {isSent && request.status === 'pending' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCancel(request.id)}
                  className="text-gray-600"
                >
                  <X className="h-3 w-3 mr-1" />
                  {t('collaboration_requests.cancel_btn')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
        <span className="ml-2 text-gray-500">{t('collaboration_requests.loading_requests')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-yellow-700">{stats.pending_received}</p>
              <p className="text-xs text-yellow-700">{t('collaboration_requests.stat_pending_received')}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.pending_sent}</p>
              <p className="text-xs text-blue-600">{t('collaboration_requests.stat_pending_sent')}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.accepted}</p>
              <p className="text-xs text-green-700">{t('collaboration_requests.stat_accepted')}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
              <p className="text-xs text-gray-600">{t('collaboration_requests.stat_total')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Sent/Received */}
      <Tabs defaultValue="received" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="received" className="flex items-center gap-1">
              <Inbox className="h-4 w-4" />
              {t('collaboration_requests.tab_received')}
              {requests.received.filter(r => r.status === 'pending').length > 0 && (
                <Badge className="ml-1 bg-red-600 text-white text-xs px-1.5 py-0">
                  {requests.received.filter(r => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-1">
              <Send className="h-4 w-4" />
              {t('collaboration_requests.tab_sent')}
            </TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" onClick={fetchRequests}>
            <RefreshCw className="h-4 w-4 mr-1" />
            {t('collaboration_requests.refresh_btn')}
          </Button>
        </div>

        <TabsContent value="received" className="space-y-3">
          {requests.received.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="py-8 text-center text-gray-500">
                <Inbox className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>{t('collaboration_requests.no_received_title')}</p>
                <p className="text-sm mt-1">{t('collaboration_requests.no_received_hint')}</p>
              </CardContent>
            </Card>
          ) : (
            requests.received.map(request => (
              <RequestCard key={request.id} request={request} isSent={false} />
            ))
          )}
        </TabsContent>

        <TabsContent value="sent" className="space-y-3">
          {requests.sent.length === 0 ? (
            <Card className="bg-gray-50">
              <CardContent className="py-8 text-center text-gray-500">
                <Send className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>{t('collaboration_requests.no_sent_title')}</p>
                <p className="text-sm mt-1">{t('collaboration_requests.no_sent_hint')}</p>
              </CardContent>
            </Card>
          ) : (
            requests.sent.map(request => (
              <RequestCard key={request.id} request={request} isSent={true} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Response Dialog */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              {t('collaboration_requests.respond_dialog_title')}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-sm">{selectedRequest.title}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('collaboration_requests.from_prefix', { name: selectedRequest.sender_name })}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t('collaboration_requests.response_message_optional')}</Label>
                <Textarea
                  placeholder={t('collaboration_requests.response_message_placeholder')}
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  rows={3}
                />
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResponseDialog(false)}
                  disabled={responding}
                >
                  {t('collaboration_requests.cancel_btn')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleRespond(false)}
                  disabled={responding}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {responding ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-1" />
                      {t('collaboration_requests.decline_btn')}
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleRespond(true)}
                  disabled={responding}
                  className="bg-green-700 hover:bg-green-800"
                >
                  {responding ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {t('collaboration_requests.accept_btn')}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollaborationRequests;
